import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Breadcrumb } from '../../common/atoms';
import { PeriodPicker } from '../../common/molecules';
import { EmptyState, SkeletonCard } from '../../tailwind/atoms';
import { analyticsApi, utilizationApi } from '../../../services';
import { handleAppError } from '../../handlers';
import ContractsManager from '../../../managers/ContractsManager';
import { AppStateContext } from '../../../utils/contexts';

import { fmtCount } from './format';
import useHealthTooltip from './useHealthTooltip';
import useLazySection from './useLazySection';
import ReportLoader, { DEFICIENCY_STAGES } from './components/ReportLoader';
import ReportHero from './sections/ReportHero';
import ChapterNav from './sections/ChapterNav';
import ChapterHeader from './sections/ChapterHeader';
import MetabolicAtAGlance from './sections/MetabolicAtAGlance';
import MetabolicDistribution from './sections/MetabolicDistribution';
import MetabolicByAge from './sections/MetabolicByAge';
import MetabolicByGender from './sections/MetabolicByGender';
import MetabolicDrivers from './sections/MetabolicDrivers';
import CoverageCaveat from './sections/CoverageCaveat';
import DeficiencyAtAGlance from './sections/DeficiencyAtAGlance';
import DeficiencyPrevalence from './sections/DeficiencyPrevalence';
import DeficiencyByAge from './sections/DeficiencyByAge';
import DeficiencyByGender from './sections/DeficiencyByGender';
import DeficiencyBurden from './sections/DeficiencyBurden';
import CoOccurrence from './sections/CoOccurrence';
import SymptomImpact from './sections/SymptomImpact';
import Benchmarks, { isBenchmarksEmpty } from './sections/Benchmarks';

// The backend reports whether enough employees were screened for the percentages to mean anything.
// Treated as sufficient when the key is absent, so an older cached payload still renders.
const sufficient = (chapter) => chapter.eligibility?.sufficient !== false;

/**
 * Copy for the screened-record gate.
 *
 * The threshold is a flat count of completed health checks, so the sentence names the workforce it was
 * counted out of: "180 of this company's 823 employees … needs at least 300" lets the reader see how
 * far off the company is, where a count with no denominator does not.
 *
 * `workforce` arrived with the threshold change, so a payload cached before it does not carry the
 * denominator — that clause drops rather than rendering a missing field.
 */
const insufficientDescription = ({ screened, workforce, min_screened: minScreened }) => {
  const completed = workforce
    ? `${fmtCount(screened)} of this company's ${fmtCount(workforce)} employees have completed a health `
      + 'check in the selected period. '
    : `${fmtCount(screened)} of this company's employees have completed a health check in the selected `
      + 'period. ';

  return `${completed}Cohort analysis needs at least ${fmtCount(minScreened)} completed health checks `
    + 'before the percentages mean anything. Try a different period, or come back once more employees '
    + 'have been screened.';
};

const ChapterSkeleton = () => (
  <div className='grid gap-ds-5'>
    <SkeletonCard className='h-[220px] bg-white' />
    <div className='grid grid-cols-1 gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <SkeletonCard className='h-[380px] bg-white' />
      <SkeletonCard className='h-[380px] bg-white' />
    </div>
  </div>
);

/**
 * Health analytics — the workforce risk report.
 *
 * Loads in three phases, chained rather than parallel: each request costs the backend one aggregate
 * scan over every lab result in the period. Chapter 01 comes with the page, the benchmarks table
 * follows it because it opens the report, and chapter 02 comes last — or earlier, if the reader scrolls
 * to it first.
 */
const HealthAnalytics = () => {
  const { companyId } = useContext(AppStateContext) || {};
  const [contracts, setContracts] = useState(null);
  const [contract, setContract] = useState(null);
  const [metabolic, setMetabolic] = useState(null);
  const [deficiency, setDeficiency] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [chapterOneError, setChapterOneError] = useState(false);
  const [chapterTwoError, setChapterTwoError] = useState(false);
  const [benchmarksError, setBenchmarksError] = useState(false);
  // Set to the server's reason when this company is not eligible for the report at all.
  const [unavailable, setUnavailable] = useState(null);
  const source = useRef(null);
  // Phase 2 gets its own cancel token. Sharing phase 1's meant any phase-1 re-run cancelled an in-flight
  // chapter-02 request, and the cancel branch below is deliberately silent — so the chapter sat on
  // skeletons with no error and no retry.
  const deficiencySource = useRef(null);
  const deficiencyRequest = useRef(null);
  const benchmarksSource = useRef(null);
  const benchmarksRequest = useRef(null);

  useHealthTooltip();

  const orgName = sessionStorage.getItem('company_name') || '';
  const reportPeriod = contract ? contract.text : '';
  const periodKey = contract ? `${contract.contract_id}-${contract.start_date}-${contract.end_date}` : 'none';

  useEffect(() => {
    let active = true;
    utilizationApi.fetchContracts(
      {},
      (response) => {
        if (!active) return;
        const list = new ContractsManager(response.data, 'Health-checks').formattedData;
        setContracts(list);
        setContract(list[0] || null);
      },
      (error) => {
        if (!active) return;
        setContracts([]);
        handleAppError(error);
      }
    );
    return () => {
      active = false;
    };
  }, [companyId]);

  // Keyed on the period's primitives, not the contract object: refetching the contract list hands back a
  // fresh object for the same period, and keying on that would re-fire both phases on every refetch.
  const contractId = contract ? contract.contract_id : null;
  const startDate = contract ? contract.start_date : null;
  const endDate = contract ? contract.end_date : null;

  const params = useCallback(
    (cancelToken) => ({ contractId, startDate, endDate, cancelToken }),
    [contractId, startDate, endDate]
  );

  // The benchmarks table. Requested with the page, not behind the chapters: it is the first section a
  // reader sees, and queueing it meant the top of the report filled last. A failure only loses this one
  // section, so it degrades quietly to an EmptyState without an error toast.
  const loadBenchmarks = useCallback(() => {
    if (!contractId || benchmarksRequest.current) return;

    benchmarksSource.current = axios.CancelToken.source();
    benchmarksRequest.current = analyticsApi
      .getHealthAnalyticsBenchmarks(params(benchmarksSource.current.token))
      .then((response) => {
        setBenchmarks(response.data);
        return response.data;
      })
      .catch((error) => {
        benchmarksRequest.current = null;
        if (axios.isCancel(error)) return null;
        setBenchmarksError(true);
        return null;
      });
  }, [contractId, params]);

  // Chapter 02. Requested with the page rather than on scroll, because two of the hero's four figures
  // come from it and the hero is the first thing on screen. Still guarded so it runs at most once per
  // period, whichever trigger asks first — this load, or the scroll sentinel further down.
  const loadDeficiency = useCallback(() => {
    if (!contractId || deficiencyRequest.current) return;

    deficiencySource.current = axios.CancelToken.source();
    deficiencyRequest.current = analyticsApi
      .getHealthAnalyticsDeficiency(params(deficiencySource.current.token))
      .then((response) => {
        setDeficiency(response.data);
        return response.data;
      })
      .catch((error) => {
        // Release the guard so a re-arm or a print can ask again, instead of the chapter being stuck on
        // a request that will never resolve.
        deficiencyRequest.current = null;
        if (axios.isCancel(error)) return null;
        setChapterTwoError(true);
        handleAppError(error);
        return null;
      });
  }, [contractId, params]);

  // All three requests go out together on page load.
  //
  // They used to be chained — chapter 01, then chapter 02, then benchmarks — to keep one aggregate scan
  // in flight at a time. The cost of that landed on the reader: the hero's deficiency figures and the
  // benchmarks table at the top of the page both filled in visibly late, one request behind the content
  // beside them. They read no data from each other, so nothing but backend load was holding them back,
  // and each response is cached per period on the backend, so the scans are paid once and not per view.
  useEffect(() => {
    if (!contractId) return undefined;

    if (source.current) source.current.cancel();
    if (deficiencySource.current) deficiencySource.current.cancel();
    if (benchmarksSource.current) benchmarksSource.current.cancel();
    source.current = axios.CancelToken.source();
    setMetabolic(null);
    setDeficiency(null);
    setBenchmarks(null);
    setChapterOneError(false);
    setChapterTwoError(false);
    setBenchmarksError(false);
    setUnavailable(null);
    deficiencyRequest.current = null;
    benchmarksRequest.current = null;

    loadDeficiency();
    loadBenchmarks();

    analyticsApi
      .getHealthAnalyticsMetabolic(params(source.current.token))
      .then((response) => {
        setMetabolic(response.data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;

        // A 422 is the backend saying this company is not eligible for the report — a workforce below
        // the cohort-analysis threshold, or digitization disabled. That is an expected answer rather
        // than a failure, so it gets its own message and no error toast.
        if (error.response && error.response.status === 422) {
          setUnavailable((error.response.data && error.response.data.error) || '');
          return;
        }

        setChapterOneError(true);
        handleAppError(error);
      });

    return () => {
      if (source.current) source.current.cancel();
      if (deficiencySource.current) deficiencySource.current.cancel();
      if (benchmarksSource.current) benchmarksSource.current.cancel();
    };
  }, [contractId, params, loadDeficiency, loadBenchmarks]);

  const sentinelRef = useLazySection(loadDeficiency, periodKey);

  const hasContracts = contracts && contracts.length > 0;
  // Benchmarks renders nothing when the contract's entitlement covers none of its markers — asked of the
  // section itself, so what counts as empty is defined once. It opens the report, so when it is absent
  // chapter 01 takes the leading gap rather than the wider gap that separates one chapter from the next.
  const benchmarksEmpty = !!benchmarks && isBenchmarksEmpty(benchmarks);

  return (
    <div
      className='ds-report -mx-4 -my-6 min-h-full bg-gray-100 px-ds-6 pb-16 pt-ds-8 sm:-mx-6 sm:-my-8 lg:px-12 lg:pb-24 lg:pt-11'
    >
      <div className='mx-auto max-w-[1240px]'>
        {/* Breadcrumb left, period control right, bottom-aligned so the two share a baseline — the picker
            carries a label above it. The pill that used to repeat the selected period is gone with the tab
            row: the picker states the period itself, so keeping the pill said the same thing twice. */}
        <div className='mb-ds-8 flex flex-wrap items-end gap-ds-4'>
          <Breadcrumb
            list={[
              { text: 'Analytics', link: '#' },
              { text: 'Health analytics', link: '/health-analytics' },
            ]}
          />
          <PeriodPicker
            list={contracts}
            currentPage={reportPeriod}
            setContract={setContract}
            loading={contracts === null}
            classes='ml-auto'
          />
        </div>

        {contracts && !hasContracts ? (
          <EmptyState
            title='No health-check period available'
            description='This company has no contract with health checks enabled, so there is nothing to report on yet.'
          />
        ) : null}

        {hasContracts && !metabolic && !chapterOneError && unavailable === null ? (
          <>
            <ReportLoader />
            <ChapterSkeleton />
          </>
        ) : null}

        {/* The tab is offered to every company, so this is where a company that does not qualify for the
            report lands. The reason is the server's, so the thresholds are stated in one place. */}
        {unavailable !== null ? (
          <EmptyState
            title={`This feature is not available for ${orgName || 'this company'}`}
            description={
              unavailable
              || 'Health analytics reads the workforce as a clinical cohort, which needs a larger '
                 + 'employee base than this company currently has.'
            }
          />
        ) : null}

        {chapterOneError ? (
          <EmptyState
            title='Could not load the metabolic risk chapter'
            description='The request failed. Refresh the page or pick a different reporting period to try again.'
          />
        ) : null}

        {/* The whole report is withheld below the screened-record threshold, not just the charts: every
            figure on the page — including the hero's headline percentages — would be arithmetic over a
            handful of people, where one employee moving between bands swings a number by whole
            percentage points. The backend decides sufficiency so the threshold is stated in one place. */}
        {metabolic && !sufficient(metabolic) ? (
          <EmptyState
            title='Not enough health checks to report on yet'
            description={insufficientDescription(metabolic.eligibility)}
          />
        ) : null}

        {metabolic && sufficient(metabolic) ? (
          <>
            <ReportHero orgName={orgName} metabolic={metabolic} deficiency={deficiency} />
            <ChapterNav />

            {/* Benchmarks opens the report: it answers "how do we compare" before the chapters spend
                pages on what the markers mean. Its own failure loses only this section, so it degrades
                to an EmptyState in place rather than touching the chapters below.

                The anchor lives on this wrapper rather than on the table, because the table is the last
                of the three requests to land and renders nothing at all without rows — as the nav's
                first pill, `#benchmarks` has to resolve while the section is still a skeleton too. */}
            <div id='benchmarks' className='scroll-mt-[130px]'>
              {benchmarksError ? (
                <EmptyState
                  className='mt-ds-9'
                  title='Could not load the industry benchmarks'
                  description='The chapters below are unaffected. Refresh the page to retry this section.'
                />
              ) : null}

              {!benchmarks && !benchmarksError ? (
                <div className='mt-ds-9'>
                  <SkeletonCard className='h-[420px] bg-white' />
                </div>
              ) : null}

              {benchmarks ? (
                <div className='ds-anim-drop-in'>
                  <Benchmarks data={benchmarks} orgName={orgName} />
                </div>
              ) : null}
            </div>

            {/* scroll-mt clears the app's fixed 64px navbar plus the sticky chapter nav, so a jump from
                the nav lands on the chapter title rather than under it. */}
            <section id='ch1' className={`scroll-mt-[130px] ${benchmarksEmpty ? 'mt-ds-9' : 'mt-20'}`}>
              <ChapterHeader title='Metabolic syndrome risk' />
              <MetabolicAtAGlance data={metabolic} />
              <MetabolicDistribution data={metabolic} />
              <MetabolicByAge data={metabolic} />
              <MetabolicByGender data={metabolic} />
              <MetabolicDrivers data={metabolic} />
              <CoverageCaveat data={metabolic} />
            </section>

            {/* Chapter-02 trigger: entering the 600px margin around this node starts its request, if the
                benchmarks chain has not already asked for it. */}
            <div ref={sentinelRef} aria-hidden className='h-px' />

            <section id='ch2' className='mt-20 scroll-mt-[130px]'>
              <ChapterHeader
                title='Deficiency & anaemia risk'
                subtitle={
                  deficiency
                    ? deficiency.markers.map((marker) => marker.label).join(' · ')
                    : 'Vitamin D · Vitamin B12 · Thyroid · Haemoglobin'
                }
              />

              {chapterTwoError ? (
                <EmptyState
                  className='mt-ds-8'
                  title='Could not load the deficiency chapter'
                  description='Chapter 01 above is unaffected. Refresh the page to retry this section.'
                />
              ) : null}

              {!deficiency && !chapterTwoError ? (
                <div className='mt-ds-8'>
                  <ReportLoader stages={DEFICIENCY_STAGES} compact />
                  <ChapterSkeleton />
                </div>
              ) : null}

              {deficiency && deficiency.cohort.total === 0 ? (
                <EmptyState
                  className='mt-ds-8'
                  title='No deficiency screening in this period'
                  description='None of the vitamin, thyroid or haemoglobin markers were reported for this company in the selected period.'
                />
              ) : null}

              {deficiency && deficiency.cohort.total > 0 ? (
                <div className='ds-anim-drop-in'>
                  <DeficiencyAtAGlance data={deficiency} />
                  <DeficiencyPrevalence data={deficiency} />
                  <DeficiencyByAge data={deficiency} />
                  <DeficiencyByGender data={deficiency} />
                  <DeficiencyBurden data={deficiency} />
                  <CoOccurrence data={deficiency} />
                  <SymptomImpact data={deficiency} />
                </div>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default HealthAnalytics;
